import { AppointmentApiService } from '@api_patient/appointment_api.service';
import { I18nAndL10n } from '@application/i18n/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { Button, ButtonArrange, CellulaFormSubmitData, Flex, Form, InputRating, InputTextArea, Text, toast } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { AnimateChangeInHeight } from '@component/animate_change_in_height.component';
import { Async, AsyncSignalValue } from '@component/async.component';
import { Shimmer } from '@component/new_shimmer.component';
import { TopBar } from '@component/top_bar.component';
import { numberSafeParse } from '@module/safe_number_parse';
import { signal } from '@preact/signals-react';
import type { SurveyAnswerValueType, SurveyQuestionAnswerApiModel } from '@visiba/api-vcs-client-v2';
import { ControllerProtocol, Injectable, useResolve, useResolveController } from '@visiba/instantiation';
import styled from 'styled-components';
import { AppointmentController } from '../view_appointment.controller';
import { BackgroundOutlet } from './_background_outlet';
import { ScreenEvaluationHcp } from './screen_evaluation_hcp.component';

export function ScreenEvaluation() {
	const appointmentController = useResolveController(AppointmentController, '');

	if (appointmentController.appointmentInfo.value?.isHcp) {
		return <ScreenEvaluationHcp />;
	}

	return <ScreenEvaluationPatient />;
}

function ScreenEvaluationPatient() {
	const appointmentController = useResolve(AppointmentController);
	const controller = useResolveController(EvaluationController);

	function handleGoBackIntercept(event: Event) {
		event.preventDefault();
		appointmentController.handleReconnect();
	}

	function handleSkipClick() {
		controller.skip();
	}

	return (
		<Form.Root
			onSubmit={(d) => controller.submit(d)}
			options={{
				defaultValues: {},
			}}
		>
			<BackgroundOutlet>
				<TopBar.Outlet />
				<TopBar.Content goBack goBackIntercept={handleGoBackIntercept} />

				<Flex
					grow='1'
					style={{
						margin: `0 auto`,
						width: '100%',
						maxWidth: '600px',
					}}
					gap={tokens.size.x5}
				>
					<Flex gap={tokens.size.x1}>
						<Text.h1 variant='headingMedium'>{withTranslation('View.Appointment.ScreenEvaluation.Title')}</Text.h1>
						<Text.p variant='bodyRegular'>{withTranslation('View.Appointment.ScreenEvaluation.Description')}</Text.p>
					</Flex>

					<AnimateChangeInHeight>
						<Async derive={controller.state} fallback={<DynamicFormRootSkeleton />} fallbackDelay={0}>
							{(questions) => (
								<Flex gap={tokens.size.x4}>
									{questions.map((x) => {
										switch (x.type) {
											case 'Int4SatisfactionScore':
												return <QuestionTypeInt4SatisfactionScore key={x.alias} question={x} />;
											case 'Text500':
												return <QuestionTypeText500 key={x.alias} question={x} />;
											default:
												return null;
										}
									})}
								</Flex>
							)}
						</Async>
					</AnimateChangeInHeight>

					<Flex grow={1} justify='flex-end' gap={tokens.size.x3}>
						<Form.Error />
						<ButtonArrange>
							<Button variant='secondary' onClick={handleSkipClick}>
								{withTranslation('View.Appointment.ScreenEvaluation.Skip')}
							</Button>
							<Form.SubmitButton>{withTranslation('View.Appointment.ScreenEvaluation.Continue')}</Form.SubmitButton>
						</ButtonArrange>
					</Flex>
				</Flex>
			</BackgroundOutlet>
		</Form.Root>
	);
}

interface QuestionTypeInt4SatisfactionScoreProps {
	question: Question;
}

const StyledRatingWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin: ${tokens.size.x5} 0;

	fieldset {
		align-items: center;
	}
`;

function QuestionTypeInt4SatisfactionScore(props: QuestionTypeInt4SatisfactionScoreProps) {
	return (
		<StyledRatingWrapper>
			<InputRating.Root direction='horizontal' label={props.question.text} name={props.question.alias} max={4} />
		</StyledRatingWrapper>
	);
}

interface QuestionTypeText500Props {
	question: Question;
}

function QuestionTypeText500(props: QuestionTypeText500Props) {
	return (
		<InputTextArea
			label={props.question.text}
			name={props.question.alias}
			minHeight={200}
			alwaysShowCharacterCount
			characterCount={500}
			options={{
				maxLength: 500,
			}}
		/>
	);
}

@Injectable()
class EvaluationController implements ControllerProtocol {
	public state = signal<AsyncSignalValue<Question[]>>({
		value: null,
		flowState: 'pending',
	});

	constructor(
		private readonly appointmentApiService: AppointmentApiService,
		private readonly universalConfig: UniversalConfig,
		private readonly appointmentController: AppointmentController,
	) {
		// Empty
	}

	async whenMount() {
		const result = await this.appointmentApiService.survey(this.universalConfig.selectedUnitId);
		if (result.err) {
			this.state.value = {
				flowState: 'error',
				value: null,
			};

			return;
		}

		this.state.value = {
			flowState: 'accessible',
			value: result.ok.data.Questions.map((x) => ({
				id: x.Id,
				alias: x.Alias,
				text: x.Text ?? '',
				sortValue: x.SortValue,
				type: x.Type,
			})),
		};
	}

	public async skip() {
		const result = await this.sendSurveyAnswers([]);
		if (result.err) return;

		this.appointmentController.handleSurveyEnd();
	}

	public async submit(data: CellulaFormSubmitData) {
		const answers = Object.entries(data)
			.filter(([, value]) => value != null && value !== '')
			.map(([key, value]) => this.createSurveyQuestionAnswer(key, value));

		const result = await this.sendSurveyAnswers(answers);
		if (result.err) return;

		this.appointmentController.handleSurveyEnd();
	}

	private async sendSurveyAnswers(answers: SurveyQuestionAnswerApiModel[]): AsyncResult {
		const payload = {
			AppointmentTicket: this.appointmentController.savedTicket ?? '',
			Answers: answers,
		};

		const result = await this.appointmentApiService.surveyAnswers(this.universalConfig.selectedUnitId, payload);
		if (result.err) {
			toast({
				label: I18nAndL10n.translate('PW.Common.Error.Unknown'),
				variant: 'danger',
			});

			return Err(result.err);
		}

		return Ok();
	}

	private createSurveyQuestionAnswer(alias: string, value: string): SurveyQuestionAnswerApiModel {
		const parsed = numberSafeParse(value);
		if (parsed != null) {
			return {
				Alias: alias,
				IntValue: parsed,
				StringValue: null,
			};
		}

		return {
			Alias: alias,
			IntValue: null,
			StringValue: value,
		};
	}
}

interface Question {
	id: number;
	alias: string;
	text: string;
	sortValue: number;
	type: SurveyAnswerValueType;
}

function DynamicFormRootSkeleton() {
	return (
		<Flex
			gap={tokens.spacing.x3}
			style={{
				height: '400px',
			}}
		>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x3} width='60%' />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='30%' />
				<Shimmer height={tokens.size.x9} />
			</Flex>
			<Flex gap={tokens.spacing.x1}>
				<Shimmer height={tokens.size.x2} width='42%' />
				<Shimmer height={tokens.size.x5} />
			</Flex>
		</Flex>
	);
}
